import { Modal, ModalBody, Steps } from "@appquality/appquality-design-system";
import { FormikProps, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePrevious } from "src/hooks/usePrevious";
import {
  closeAddDeviceModal,
  closeEditDeviceModal,
  selectDevice
} from "src/pages/Devices/userDevicesSlice";
import { useAppDispatch, useAppSelector } from "src/store";
import styled from "styled-components";
import { DeviceDetails } from "./DeviceDetails";
import { DeviceModalFooter } from "./DeviceModalFooter";
import { DeviceModalForm } from "./DeviceModalForm";
import { DeviceRecap } from "./DeviceRecap";
import DeviceType from "./DeviceType";

const DeviceWizard = styled.div`
  .device-wizard-footer {
    display: grid;
    grid-template-areas: "prev next";
    grid-template-columns: 1fr 1fr;
    grid-gap: ${(props) => props.theme.grid.spacing.default};

    @media (min-width: ${(props) => props.theme.grid.breakpoints.lg}) {
      grid-template-areas: "empty prev next";
      grid-template-columns: 1fr auto auto;
    }
    button {
      min-width: 120px;
    }
    button:first-child {
      grid-area: prev;
    }
    button:last-child {
      grid-area: next;
    }
  }
`;

const EditDeviceModal = () => {
  const { current } = useAppSelector((state) => state.userDevices);
  const { isEditModalOpen, isAddModalOpen } = useAppSelector(
    (state) => state.userDevices
  );
  const dispatch = useAppDispatch();
  const [step, setStep] = useState(0);
  const { t } = useTranslation();
  const modalOpen = current ? isEditModalOpen : isAddModalOpen;
  const closeEditModal = () => {
    dispatch(selectDevice(undefined));
    dispatch(closeEditDeviceModal());
  };
  const closeModal = () => {
    setStep(0);
    current ? closeEditModal() : dispatch(closeAddDeviceModal());
  };
  let steps: WizardStep[] = [
    {
      title: t("Device details"),
      content: <DeviceDetails edit={!!current} />,
      isCompleted: (errors) =>
        !errors.operating_system_id && step > (current ? 0 : 1),
    },
    { title: t("Device recap"), content: <DeviceRecap /> },
  ];
  if (!current) {
    steps = [
      {
        title: t("Device type"),
        content: <DeviceType />,
        isCompleted: (errors) => {
          return !errors.device_type && step > 0;
        },
      },
      ...steps,
    ];
  }

  return (
    <DeviceWizard>
      <DeviceModalForm step={step} closeModal={closeModal}>
        {(formikProps: FormikProps<any>) => {
          return (
            <Wrapper step={step} modalOpen={modalOpen}>
              <Modal
                closeOnClickOutside={false}
                isOpen={modalOpen}
                onClose={() => {
                  closeModal();
                  formikProps.handleReset();
                }}
                title={current ? t("Edit device") : t("Add a new device")}
                footer={
                  <DeviceModalFooter
                    isValid={formikProps.isValid}
                    currentStep={step}
                    steps={steps}
                    setStep={setStep}
                    onSubmit={formikProps.handleSubmit}
                  />
                }
              >
                <ModalBody>
                  <Steps current={step} className="aq-mb-3">
                    {steps.map((step, index) => (
                      <Steps.Step
                        key={index}
                        className="device-wizard-step"
                        isCompleted={
                          step.isCompleted &&
                          step.isCompleted(formikProps.errors)
                        }
                        title={step.title}
                      />
                    ))}
                  </Steps>
                  <div className="device-wizard-content">
                    {steps[step].content}
                  </div>
                </ModalBody>
              </Modal>
            </Wrapper>
          );
        }}
      </DeviceModalForm>
    </DeviceWizard>
  );
};

const Wrapper = ({
  step,
  modalOpen,
  children,
}: {
  step: number;
  modalOpen: boolean;
  children: React.ReactNode;
}) => {
  const { validateForm, handleReset } = useFormikContext<any>();

  const prevModalOpen: boolean = usePrevious<boolean>(modalOpen);
  useEffect(() => {
    validateForm();
  }, [step]);
  useEffect(() => {
    // if passing from close to open
    if (!prevModalOpen && modalOpen) {
      handleReset();
    }
  }, [modalOpen]);

  return <>{children}</>;
};

export default EditDeviceModal;
